import { useContext, type JSX } from 'react';
import queryDynamic from './EkonomiskAnalysQuery.gql';
import type {
  AnalysQueryDynamicVariables as IVariables,
  AnalysQueryDynamic as IData,
  AnalysQueryDynamic_ekanalysresultatperanalys_columns_data,
} from './_QueryTypes/AnalysQueryDynamic';
import { ISumData } from '../EkonomiskAnalysSettingsQuery/EkonomiskAnalysSettingsQuery';
import {
  iSumDataToInputSum,
  colToInputCol,
  datesToTimeInterval,
} from '../../Utils/convertUtils';
import { TEDUrvalType } from '../../../../../../../Types/UrvalTypes';
import {
  IInternalRow,
  setData,
  getFieldOperatorData,
  sumData,
  evaluateCalculatedColumns,
  setSumValues,
  groupData,
  getDisplayType,
  calculateValue,
  IInternalCell,
} from './EkonomiskAnalysQueryUtils';
import { ekanalys_ekanalys_columns } from '../EkonomiskAnalysSettingsQuery/_QueryTypes/ekanalys';
import { IEkonomiskAnalysFilledDates } from '../../Components/AnalysSettingsHandler/AnalysSettingsHandler';
import { IColumn } from '../../Components/EkonomiskAnalysTable/EkonomiskAnalysTableBase';
import useHandledQuery from '../../../../../../../Components/QueryComponents/HandledQuery/HandledQueryHook';
import { EkonomiskAnalysContext } from '../../Components/Context/EkonomiskAnalysContext';
import { getMixedValue } from '../../../../../../../Components/QueryComponents/tedMixedUtils';
import EDAvenyProgress from '../../../../../../../BaseComponents/ProgressIndicators/AvenyProgress/EDAvenyProgress';
import EDTypography from '../../../../../../../BaseComponents/Typography/EDTypography';

export type { AnalysQueryDynamic_ekanalysresultatperanalys_columns_fieldinfo as IFieldInfo } from './_QueryTypes/AnalysQueryDynamic';

export interface ICell {
  value: string;
  hasDetails: boolean;
  fieldName?: string;
  displayType?: DisplayType;
  tooltip?: string;
  hasChildValues: boolean;
}

export interface IAnalys {
  columns: IColumn[];
  data: IRow[];
  totalSum?: IRow;
  summeringColCount?: number;
}

export interface IRow {
  data: ICell[];
  children?: IInternalRow[];
  sumValues?: string[];
}

export type DisplayType = 'percent' | 'currency';

interface IProps {
  name: string;
  title?: string;
  category?: string;
  columns: ekanalys_ekanalys_columns[];
  sums: ISumData[];
  dates: IEkonomiskAnalysFilledDates;
  codePrivilegesId: string;
  baseUrvalId?: string;
  urval?: TEDUrvalType;
  detailedSelection?: TEDUrvalType;
  endastBokfort: boolean;
  settingsChanged: boolean;
  forceHelarsjamforelse: boolean | null;
  osummeratLage?: boolean;
  visaSummeringar: boolean;
  skip?: boolean;
  fetchPolicy?: 'cache-first' | 'network-only' | 'cache-and-network';
  includeForcalculatedColumns?: boolean;
  children: (data: IAnalys, hideTable?: boolean) => JSX.Element;
  setRefetchAnalys: ((refetchAnalys: () => void) => void) | undefined;
}

const EkonomiskAnalysQuery = (props: IProps) => {
  const ekonomiskAnalysContext = useContext(EkonomiskAnalysContext);

  let analys: IAnalys = {
    columns: [],
    data: [],
  };

  const skipAnalysQueryDynamic =
    props.columns === null ||
    props.columns.length === 0 ||
    props.sums === null ||
    props.sums.length === 0 ||
    props.settingsChanged;

  const queryResult = useHandledQuery<IData, IVariables>(
    queryDynamic,
    {
      variables: {
        analys: {
          name: props.name,
          rubrik: props.title,
          kategori: props.category,
          columns: props.columns.map(colToInputCol),
          sums: props.sums.map(iSumDataToInputSum),
          endast_bokfort: props.endastBokfort,
          force_helarsjamforelse: props.forceHelarsjamforelse,
        },
        tidsintervall: datesToTimeInterval(props.dates),
        codePrivilegesId: props.codePrivilegesId,
        baseSelectionId: props.baseUrvalId,
        userSelection: props.urval,
        detailedSelection: props.detailedSelection,
      },
      fetchPolicy: props.fetchPolicy ? props.fetchPolicy : 'cache-and-network',
      notifyOnNetworkStatusChange: true,
      skip: props.skip || skipAnalysQueryDynamic,
      onCompleted: data => {
        if (
          !props.skip &&
          data.ekanalysresultatperanalys &&
          data.ekanalysresultatperanalys.columns
        ) {
          const analysResultColumns = data.ekanalysresultatperanalys.columns
            .filter(column => column.visible)
            .map((column, index) => {
              const fieldCodeValue = column.fieldinfo?.find(
                info => info.name === 'FALTKOD'
              )?.value;
              const fieldCode =
                fieldCodeValue?.__typename === 'TEDMixedString'
                  ? fieldCodeValue.asString ?? ''
                  : '';

              return {
                name: `${column.rubrik1} ${column.rubrik2} ${column.rubrik3}`,
                index,
                fieldCode: fieldCode,
              };
            });

          ekonomiskAnalysContext.setAnalysResultColumns(analysResultColumns);
        }
      },
    },
    {
      loadingComponent: <EDAvenyProgress />,
      noDataComponent: (
        <EDTypography>
          Inget data hittades, testa att analysera om eller göra ett annat urval
        </EDTypography>
      ),
    },
    { setRefetch: props.setRefetchAnalys }
  );

  if (
    props.columns === null ||
    props.columns.length === 0 ||
    props.sums === null ||
    props.sums.length === 0 ||
    props.settingsChanged
  ) {
    return <>{props.children(analys, props.settingsChanged)}</>;
  }

  if (props.skip) {
    return props.children(analys, true);
  }

  if (!queryResult.data) {
    return queryResult.renderComponent;
  }

  const data = queryResult.data;

  let analysData: IInternalRow[] = [];

  const ekanalysresultat = data.ekanalysresultatperanalys;
  const sumbens = ekanalysresultat ? ekanalysresultat.sumbens : null;
  if (
    ekanalysresultat &&
    sumbens &&
    ekanalysresultat.sums &&
    ekanalysresultat.columns
  ) {
    if (props.visaSummeringar) {
      const sums: IColumn[] = ekanalysresultat.sums.map((x, index) => {
        setData(x, analysData, sumbens[index].data);

        return {
          title1: x.rubrik1 || '',
          title2: x.rubrik2 || '',
          title3: x.rubrik3 || '',
          fieldInfo: x.fieldinfo || [],
          fieldType: x.fieldtype || '',
          visible: true,
        };
      });

      const columns: IColumn[] = ekanalysresultat.columns.map((x, i) => {
        const operatorData = getFieldOperatorData(x.fieldinfo);

        setData(x, analysData, undefined, operatorData || undefined);

        return {
          title1: x.rubrik1 || '',
          title2: x.rubrik2 || '',
          title3: x.rubrik3 || '',
          fieldInfo: x.fieldinfo || [],
          fieldType: x.fieldtype || '',
          inverted: props.columns[i]
            ? props.columns[i].VAND_TECKEN || false
            : false,
          visible: x.visible == null ? true : x.visible,
          forceCalculation: x.forceCalculation,
        };
      });

      let totalSumCells = sumData(analysData, columns.length);
      totalSumCells.unshift({
        value: 'Totalsumma',
        sumValue: '',
        hasDetails: false,
        hasChildValues: false,
      });
      let totalSumRow: IInternalRow = {
        data: totalSumCells,
      };

      let rows = groupData(analysData, columns.length + 1);

      // Go through all the rows and calculate the values for cells that have calculateData
      evaluateCalculatedColumns(rows);
      evaluateCalculatedColumns([totalSumRow], true);

      analys.columns = sums.concat(columns);
      analys.data = rows;
      analys.totalSum = totalSumRow;

      setSumValues(analys.data, []);

      // Filtrera bort så vi ej visar osynliga kolumner om inte forceCalculation är satt
      analys.columns = analys.columns.filter(col => {
        if (props.includeForcalculatedColumns) {
          return col.visible || col.forceCalculation;
        } else {
          return col.visible;
        }
      });
      filterVisible(rows, columns, false, props.includeForcalculatedColumns);
      filterVisible([totalSumRow], columns);
    } else {
      // Kommande funktionalitet
      const COMBINE_CODE_BENAMNING = false;

      const getLength = (
        column: AnalysQueryDynamic_ekanalysresultatperanalys_columns_data | null
      ): number => {
        if (column?.__typename === 'TEDMixedCurrencyArray') {
          return column?.asCurrencyArray?.length ?? 0;
        } else if (column?.__typename === 'TEDMixedStringArray') {
          return column?.asStringArray?.length ?? 0;
        }

        return 0;
      };

      const getValue = (
        column: AnalysQueryDynamic_ekanalysresultatperanalys_columns_data | null,
        index: number
      ): string => {
        if (column?.__typename === 'TEDMixedCurrencyArray') {
          return column?.asCurrencyArray?.[index] ?? '';
        } else if (column?.__typename === 'TEDMixedStringArray') {
          return column?.asStringArray?.[index] ?? '';
        } else if (column?.__typename === 'TEDMixedFloatArray') {
          return column?.asFloatArray?.[index]?.toString() ?? '';
        }

        return '';
      };
      const colData = ekanalysresultat.columns[0].data;

      let rows: IRow[] = [];
      for (let i = 0; i < getLength(colData); i++) {
        let row: IRow = { data: [] };

        // Summeringskolumner
        for (let j = 0; j < ekanalysresultat.sums.length; j++) {
          const code = getValue(ekanalysresultat.sums[j].data, i);
          const benamning = getValue(
            ekanalysresultat.sumbens?.[j].data ?? null,
            i
          );

          if (COMBINE_CODE_BENAMNING) {
            const value = `${code}${code !== '' ? '-' : ''}${benamning}`;
            row.data.push({
              hasChildValues: false,
              hasDetails: false,
              value,
            });
          } else {
            row.data.push({
              hasChildValues: false,
              hasDetails: false,
              value: code,
              tooltip: benamning,
            });
            // row.data.push({ hasDetails: false, value: benamning });
          }
        }

        // "Vanliga" kolumner
        for (let j = 0; j < ekanalysresultat.columns.length; j++) {
          const operatorData =
            getFieldOperatorData(ekanalysresultat.columns[j].fieldinfo) ??
            undefined;
          let hasData = true;
          if (ekanalysresultat.columns[j].data !== null) {
            const data = getMixedValue(
              ekanalysresultat.columns[j].data ?? {
                __typename: 'TEDMixedCurrencyArray',
                asCurrencyArray: [],
              }
            );
            if (Array.isArray(data)) {
              hasData = data.some((value: number | string | boolean | null) => {
                return value != null;
              });
            }
          }

          row.data.push({
            hasDetails: operatorData === undefined,
            displayType: getDisplayType(operatorData),
            value: getValue(ekanalysresultat.columns[j].data, i),
            hasChildValues: hasData,
          });
        }
        row.sumValues = ekanalysresultat.sums.map(sum => getValue(sum.data, i));
        rows.push(row);
      }

      const sums: IColumn[] = [];
      for (let i = 0; i < ekanalysresultat.sums.length; i++) {
        const sum = ekanalysresultat.sums[i];
        // Kod
        sums.push({
          fieldInfo: sum.fieldinfo ?? [],
          fieldType: sum.fieldtype ?? '',
          title1: sum.rubrik1 ?? '',
          title2: sum.rubrik2 ?? '',
          title3: sum.rubrik3 ?? '',
          visible: true,
        });
        // Benämning
        // if (!COMBINE_CODE_BENAMNING) {
        //   sums.push({
        //     fieldInfo: sum.fieldinfo ?? [],
        //     fieldType: sum.fieldtype ?? '',
        //     title1: sum.rubrik1 ?? '',
        //     title2: 'Benämning',
        //     title3: sum.rubrik3 ?? '',
        //     visible: true,
        //   });
        // }
      }

      const columns: IColumn[] = ekanalysresultat.columns.map(
        (column, i): IColumn => ({
          fieldInfo: column.fieldinfo ?? [],
          fieldType: column.fieldtype ?? '',
          title1: column.rubrik1 ?? '',
          title2: column.rubrik2 ?? '',
          title3: column.rubrik3 ?? '',
          inverted: props.columns[i]
            ? props.columns[i].VAND_TECKEN || false
            : false,
          visible: column.visible == null ? true : column.visible,
        })
      );

      let totalSumCells: IInternalCell[] = [];

      const sumLength = sums.length;
      for (let j = sumLength; j < sumLength + columns.length; j++) {
        let sum = 0;
        let nr = 0;
        for (let i = 0; i < rows.length; i++) {
          nr = parseFloat(rows[i].data[j].value);
          if (!isNaN(nr)) {
            sum += nr;
          }
        }

        totalSumCells.push({
          hasDetails: false,
          fieldName:
            ekanalysresultat.columns[j - sumLength].fieldname ?? undefined,
          displayType: 'currency',
          value: sum.toString(),
          hasChildValues: false,
        });
      }

      // En för varje summeringsbegrepps-kolumn (förutom första)
      for (let i = 1; i < sumLength; i++) {
        totalSumCells.unshift({
          hasChildValues: false,
          hasDetails: false,
          value: '',
        });
      }
      // Namnet för totalsumma raden
      totalSumCells.unshift({
        hasChildValues: false,
        value: 'Totalsumma',
        hasDetails: false,
      });

      const totalSumRow: IRow = {
        data: totalSumCells,
      };

      for (let i = 0; i < columns.length; i++) {
        const fieldOperatorData = getFieldOperatorData(columns[i].fieldInfo);
        if (fieldOperatorData && fieldOperatorData.operator === '%') {
          const val = calculateValue(fieldOperatorData, totalSumRow.data);
          totalSumRow.data[sumLength + i].value = val.toString();
          totalSumRow.data[sumLength + i].displayType = 'percent';
        }
      }

      analys.columns = sums.concat(columns);
      analys.data = rows;
      analys.totalSum = totalSumRow;
      analys.summeringColCount = sums.length;

      // Filtera bort så vi ej visar osynliga kolumner
      filterVisible(analys.data, analys.columns, true);
      filterVisible([analys.totalSum], analys.columns, true);
      analys.columns = analys.columns.filter(col => col.visible);
    }
  }
  return props.children(analys);
};

const filterVisible = (
  rows: IInternalRow[],
  columns: IColumn[],
  sameIndex: boolean = false,
  includeForcalculatedColumns: boolean = false
) => {
  for (let row of rows) {
    row.data = row.data.filter(
      (_, index) => {
        if (includeForcalculatedColumns) {
          return (
            index === 0 ||
            columns[index - (sameIndex ? 0 : 1)].visible ||
            columns[index - (sameIndex ? 0 : 1)].forceCalculation
          );
        } else {
          return index === 0 || columns[index - (sameIndex ? 0 : 1)].visible;
        }
      }
      // Första index är header, antar att den alltid ska visas
    );
    if (row.children) {
      filterVisible(
        row.children,
        columns,
        sameIndex,
        includeForcalculatedColumns
      );
    }
  }
};

export default EkonomiskAnalysQuery;
